import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, CircularProgress, Container, Typography } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
import Header from "../../component/users/Header";
import Footer from "../../component/users/Footer";
import CompetitionLeaderboard from "../../component/users/competitions/CompetitionLeaderboard";
import {
  getCompetitionVotersLeaderboard,
  getFashionVotersLeaderboard,
} from "../../api/votersLeaderboard";
import { getFashionShowcase } from "../../api/fashionPublic";

function toLeaderboardRow(voter) {
  const free = Number(voter.freeVotes);
  const paid = Number(voter.paidVotes);
  const hasSplit = Number.isFinite(free) && Number.isFinite(paid) && voter.freeVotes != null;
  const statsLine = hasSplit ? `${free} free · ${paid} paid` : "Votes cast";
  return {
    id: voter.id,
    rank: voter.rank,
    voteCount: Number(voter.totalVotes) || 0,
    vote_count: Number(voter.totalVotes) || 0,
    videoCount: 0,
    videos: [],
    statsLine,
    category: "Voter",
    full_name: voter.name,
    contestant: {
      full_name: voter.name,
      profile_image: voter.profile_img || "",
      user: {
        firstName: voter.name,
        profile_img: voter.profile_img || "",
      },
    },
  };
}

export default function VotersLeaderboardPage() {
  const { id, designerId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("mynetgoals.com-token");
  const isFashion = Boolean(designerId);
  const [title, setTitle] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        if (isFashion) {
          const [votersRes, showcaseRes] = await Promise.all([
            getFashionVotersLeaderboard(token, designerId),
            getFashionShowcase(token, designerId),
          ]);
          if (cancelled) return;
          const designer = showcaseRes.data?.data?.designer;
          setTitle(designer?.title || designer?.name || "Fashion Designer");
          setRows((votersRes.data?.data || []).map(toLeaderboardRow));
        } else {
          const [votersRes, detailRes] = await Promise.all([
            getCompetitionVotersLeaderboard(token, id),
            axios.get(
              `${process.env.REACT_APP_API_URL_USER}/getCompetitionDetail/${id}`,
              { headers: { Authorization: `Bearer ${token}` } }
            ),
          ]);
          if (cancelled) return;
          setTitle(detailRes.data?.data?.name || "Competition");
          setRows((votersRes.data?.data || []).map(toLeaderboardRow));
        }
      } catch (error) {
        if (!cancelled) {
          setRows([]);
          const status = error?.response?.status;
          if (status !== 404) {
            toast.error(error?.response?.data?.message || "Failed to load voters leaderboard");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [token, id, designerId, isFashion]);

  const backTo = isFashion ? `/fashion-designers/${designerId}` : `/competitions/${id}`;

  return (
    <>
      <ToastContainer />
      <Header />
      <Box className="userLayout">
        <Container
          disableGutters
          maxWidth="lg"
          sx={{
            background: "#242743",
            minHeight: "85vh",
            pb: 4,
            width: "100%",
            overflowX: "hidden",
          }}
        >
          <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, pt: 2.5 }}>
            <Button
              startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />}
              onClick={() => navigate(backTo)}
              sx={{
                color: "rgba(255,255,255,0.75)",
                textTransform: "none",
                mb: 2,
              }}
            >
              {isFashion ? "Back to collection" : "Back to competition"}
            </Button>

            <Typography
              sx={{
                color: "#fff",
                fontWeight: 800,
                fontSize: { xs: 22, sm: 26, md: 30 },
                fontFamily: "Montserrat, sans-serif",
                mb: 0.5,
                lineHeight: 1.2,
                wordBreak: "break-word",
              }}
            >
              Voters Leaderboard
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.55)", mb: 2.5, fontSize: { xs: 13, sm: 16 } }}>
              {title ? `${title} · ` : ""}
              who has cast the most votes
            </Typography>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress sx={{ color: "#ef364e" }} />
              </Box>
            ) : (
              <CompetitionLeaderboard
                competitionId={isFashion ? null : id}
                rows={rows}
                categoryTitle={title || "Voters"}
                emptyLabel="No voters ranked yet."
              />
            )}
          </Box>
          <Footer />
        </Container>
      </Box>
    </>
  );
}
